import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import { unidadMedidaService } from '../services/api';
import '../styles/datatable.css';
import { getErrorDetail } from '../utils/errorHelper';

const UnidadesMedida = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ codigo: '', codigo_sunat: '', descripcion: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await unidadMedidaService.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar unidades de medida.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const codigo = row['Codigo'] || row['codigo'] || '';
        const codigo_sunat = row['Codigo SUNAT'] || row['codigo_sunat'] || '';
        const descripcion = row['Descripcion'] || row['descripcion'] || '';
        let errorMsg = null;
        if (!codigo && !descripcion) {
          fail++; errorMsg = `Fila ${i + 1}: Codigo y Descripcion vacios`;
        } else if (!codigo) {
          fail++; errorMsg = `Fila ${i + 1}: Codigo vacio`;
        } else if (!descripcion) {
          fail++; errorMsg = `Fila ${i + 1}: Descripcion vacia`;
        } else {
          try {
            await unidadMedidaService.create({ codigo: String(codigo), codigo_sunat: String(codigo_sunat), descripcion: String(descripcion) });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} (${codigo}): ${getErrorDetail(err, 'Error al crear unidad')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchData();
    };
    return () => { window._dtImportHandler = null; };
  }, []);

  const openCreate = () => { setForm({ codigo: '', codigo_sunat: '', descripcion: '' }); setEditing(null); setShowModal(true); };
  const openEdit = (r) => { setEditing(r); setForm({ codigo: r.codigo, codigo_sunat: r.codigo_sunat || '', descripcion: r.descripcion }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo || !form.descripcion) { setAlert({ type: 'warning', message: 'Codigo y descripcion son obligatorios.' }); return; }
    try {
      if (editing) {
        await unidadMedidaService.update(editing.id_unidad, form);
      } else {
        await unidadMedidaService.create(form);
      }
      setShowModal(false); fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al guardar.' });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Unidad', message: `¿Eliminar "${r.descripcion}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try { await unidadMedidaService.delete(r.id_unidad); setToast('Unidad eliminada'); fetchData(); }
        catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); }
      },
    });
  };

  const columns = [
    { key: 'codigo', label: 'Codigo', width: 100, style: { fontWeight: 600 } },
    { key: 'codigo_sunat', label: 'Codigo SUNAT', width: 120 },
    { key: 'descripcion', label: 'Descripcion' },
  ];

  const importCols = [
    { key: 'Codigo', label: 'Codigo', required: true, example: 'UND', description: 'Codigo abreviado de la unidad (max 20 caracteres)' },
    { key: 'Codigo SUNAT', label: 'Codigo SUNAT', required: false, example: 'NIU', description: 'Codigo SUNAT de la unidad de medida' },
    { key: 'Descripcion', label: 'Descripcion', required: true, example: 'Unidad', description: 'Nombre completo de la unidad de medida' },
  ];

  return (
    <Layout title="Unidades de Medida" subtitle="Tablas Generales">
      <div className="page-header">
        <h2>Unidades de Medida</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nueva</button>
      </div>

      <DataTable
        columns={columns}
        data={records}
        idField="id_unidad"
        onEdit={openEdit}
        onDelete={handleDelete}
        loading={loading}
        emptyMessage="No hay registros"
        exportFileName="unidades_medida"
        importColumns={importCols}
      />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <h2>{editing ? 'Editar Unidad' : 'Unidad de Medida'}</h2>
                {!editing && (
                  <span className="modal-header-badge">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg>
                  </span>
                )}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row"><label>Codigo *</label><input value={form.codigo} onChange={e => setForm({...form, codigo: e.target.value})} placeholder="Ej: UND" autoFocus /></div>
                <div className="form-row"><label>Codigo SUNAT</label><input value={form.codigo_sunat} onChange={e => setForm({...form, codigo_sunat: e.target.value})} placeholder="Ej: NIU" /></div>
                <div className="form-row"><label>Descripcion *</label><input value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} placeholder="Ej: Unidad" /></div>
              </div>
              <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button><button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button></div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default UnidadesMedida;
